import React, { useCallback, useEffect, useState } from 'react';
import { Brain, Loader2, Plus } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { toast } from 'sonner';
import MemoryNoteRow from '@/components/writer/MemoryNoteRow';
import {
  addMemoryNote,
  clearMemoryNotes,
  deleteMemoryNote,
  formatCommaList,
  groupNotesByKind,
  loadMemoryNotes,
  loadUserPreferences,
  parseCommaList,
  saveUserPreferences,
  updateMemoryNote,
  type MemoryNote,
} from '@/lib/writer/memoryClient';

interface Props {
  projectId: string;
}

/**
 * What Writer remembers about this draft.
 *
 * A reading surface, not a form: the agent takes the notes while it works,
 * the author skims them and corrects the odd one. The only settings kept
 * here are tone and banned words, because they are one line each.
 */
const WriterMemoryPanel: React.FC<Props> = ({ projectId }) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [savingPrefs, setSavingPrefs] = useState(false);
  const [notes, setNotes] = useState<MemoryNote[]>([]);
  const [newNote, setNewNote] = useState('');
  const [adding, setAdding] = useState(false);
  const [tone, setTone] = useState('');
  const [bannedWords, setBannedWords] = useState('');

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [rows, prefs] = await Promise.all([
        loadMemoryNotes(projectId),
        loadUserPreferences(),
      ]);
      setNotes(rows);
      setTone(prefs?.tone ?? '');
      setBannedWords(formatCommaList(prefs?.banned_words));
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not load memory');
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    if (open) void refresh();
  }, [open, refresh]);

  const handleAdd = useCallback(async () => {
    const text = newNote.trim();
    if (!text) return;
    setAdding(true);
    try {
      const created = await addMemoryNote(projectId, 'context', text);
      setNotes((prev) => [...prev, created]);
      setNewNote('');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not save the note');
    } finally {
      setAdding(false);
    }
  }, [newNote, projectId]);

  const handleSaveNote = useCallback(async (id: string, text: string) => {
    try {
      await updateMemoryNote(id, text);
      setNotes((prev) =>
        prev.map((n) => (n.id === id ? { ...n, note: text, origin: 'author' } : n)),
      );
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not update the note');
    }
  }, []);

  const handleDeleteNote = useCallback(async (id: string) => {
    try {
      await deleteMemoryNote(id);
      setNotes((prev) => prev.filter((n) => n.id !== id));
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not delete the note');
    }
  }, []);

  const handleClear = useCallback(async () => {
    if (!window.confirm('Delete every note Writer has taken for this draft?')) return;
    try {
      await clearMemoryNotes(projectId);
      setNotes([]);
      toast.success('Memory cleared');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not clear memory');
    }
  }, [projectId]);

  const handleSavePrefs = useCallback(async () => {
    setSavingPrefs(true);
    try {
      await saveUserPreferences({
        tone: tone.trim() || null,
        banned_words: parseCommaList(bannedWords),
      });
      toast.success('Saved');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Save failed');
    } finally {
      setSavingPrefs(false);
    }
  }, [tone, bannedWords]);

  const groups = groupNotesByKind(notes);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button
          type="button"
          aria-label="Open memory panel"
          className="inline-flex items-center gap-1.5 text-xs uppercase tracking-widest text-muted-foreground hover:text-foreground transition"
        >
          <Brain className="h-3.5 w-3.5" />
          Memory
        </button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>What Writer remembers</DialogTitle>
          <DialogDescription>
            Writer takes these notes as you work, so it keeps your preferences and
            the choices you made. Click a note to correct it.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-10">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <>
            <div className="flex items-center gap-2">
              <Input
                value={newNote}
                onChange={(e) => setNewNote(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    void handleAdd();
                  }
                }}
                placeholder="Add a note yourself, for example: submitting to NeurIPS, 8 pages"
                aria-label="Add a note"
              />
              <Button
                type="button"
                size="icon"
                variant="secondary"
                disabled={adding || !newNote.trim()}
                aria-label="Save note"
                onClick={() => void handleAdd()}
              >
                <Plus className="h-4 w-4" />
              </Button>
            </div>

            <ScrollArea className="flex-1 min-h-0 -mx-2 px-2">
              {groups.length === 0 ? (
                <p className="py-10 text-center text-sm text-muted-foreground">
                  Writer takes notes here as you work. Nothing to fill in.
                </p>
              ) : (
                <div className="space-y-5 py-2">
                  {groups.map((group) => (
                    <section key={group.kind}>
                      <h3 className="px-3 pb-1 text-xs uppercase tracking-widest text-muted-foreground">
                        {group.label}
                      </h3>
                      <ul className="space-y-0.5">
                        {group.notes.map((note) => (
                          <MemoryNoteRow
                            key={note.id}
                            note={note}
                            onSave={handleSaveNote}
                            onDelete={handleDeleteNote}
                          />
                        ))}
                      </ul>
                    </section>
                  ))}
                </div>
              )}
            </ScrollArea>

            <div className="border-t border-border/60 pt-4 space-y-3">
              <div className="grid gap-3 sm:grid-cols-2">
                <label className="block space-y-1.5">
                  <span className="text-xs uppercase tracking-widest text-muted-foreground">
                    Preferred tone
                  </span>
                  <Input
                    value={tone}
                    onChange={(e) => setTone(e.target.value)}
                    placeholder="e.g. concise and technical"
                  />
                </label>
                <label className="block space-y-1.5">
                  <span className="text-xs uppercase tracking-widest text-muted-foreground">
                    Words to avoid
                  </span>
                  <Input
                    value={bannedWords}
                    onChange={(e) => setBannedWords(e.target.value)}
                    placeholder="e.g. novel, leverage, paradigm"
                  />
                </label>
              </div>
              <div className="flex items-center justify-between">
                <button
                  type="button"
                  onClick={() => void handleClear()}
                  className="text-xs text-muted-foreground hover:text-destructive transition"
                >
                  Delete all notes
                </button>
                <Button
                  type="button"
                  size="sm"
                  disabled={savingPrefs}
                  onClick={() => void handleSavePrefs()}
                >
                  {savingPrefs ? 'Saving…' : 'Save settings'}
                </Button>
              </div>
              <p className="text-[11px] text-muted-foreground">
                Tone and words to avoid apply to every draft. Notes stay with this draft.
              </p>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default WriterMemoryPanel;
